import { useEffect, useState } from "react";
import { portalApi, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock, Check, X, Info, Loader2 } from "lucide-react";
import { BienestarEmocional } from "@/components/BienestarEmocional";
import { useIdioma } from "@/context/IdiomaContext";

const ESTADO_LABEL = {
  presente: { label: "Asistió", cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
  ausente: { label: "Falta", cls: "bg-destructive/15 text-destructive border-destructive/30" },
  justificada: { label: "Justificada", cls: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
  reposicion: { label: "Reposición", cls: "bg-primary/15 text-primary border-primary/30" },
};

export const PortalAsistencia = ({ hijo, onCambio }) => {
  const { t } = useIdioma();
  const [asistencia, setAsistencia] = useState(null);
  const [reposiciones, setReposiciones] = useState([]);
  const [opciones, setOpciones] = useState({});
  const [cargando, setCargando] = useState(null);

  const load = async () => {
    const [{ data: a }, { data: r }] = await Promise.all([
      portalApi.get(`/portal/alumnos/${hijo.id}/asistencia`),
      portalApi.get("/portal/reposiciones"),
    ]);
    setAsistencia(a);
    setReposiciones(r.filter((x) => x.alumno_id === hijo.id));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hijo.id]);

  const cargarOpciones = async (repoId) => {
    if (opciones[repoId]) return;
    setCargando(repoId);
    try {
      const { data } = await portalApi.get(`/portal/reposiciones/${repoId}/opciones`);
      setOpciones((o) => ({ ...o, [repoId]: data }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCargando(null);
    }
  };

  const agendar = async (repoId, sesionId) => {
    try {
      const { data } = await portalApi.post(`/portal/reposiciones/${repoId}/agendar`, { sesion_id: sesionId });
      toast.success(`Reposición reservada para el ${data.fecha} a las ${data.hora_inicio}`);
      setOpciones((o) => ({ ...o, [repoId]: undefined }));
      await load();
      onCambio?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const pedirRecuperacion = async (sesionId) => {
    setCargando(sesionId);
    try {
      const { data } = await portalApi.post(`/portal/alumnos/${hijo.id}/recuperar`, { sesion_id: sesionId });
      toast.success("Ya puedes elegir el día de tu recuperación");
      await load();
      await cargarOpciones(data.repo_id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCargando(null);
    }
  };

  const cancelar = async (repoId) => {
    try {
      await portalApi.post(`/portal/reposiciones/${repoId}/cancelar`);
      toast.success("Reserva anulada");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="portal-asistencia">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <Card className="rounded-2xl p-5 border-border shadow-sm" data-testid="portal-pct-asistencia">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t("portal.asistenciaPct")}</p>
          <p className="font-display text-3xl font-extrabold mt-3">{asistencia?.porcentaje_asistencia ?? 0}%</p>
        </Card>
        <Card className="rounded-2xl p-5 border-border shadow-sm">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t("portal.asistidas")}</p>
          <p className="font-display text-3xl font-extrabold mt-3">{asistencia?.totales?.presente ?? 0}</p>
        </Card>
        <Card className="rounded-2xl p-5 border-border shadow-sm">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t("portal.faltas")}</p>
          <p className="font-display text-3xl font-extrabold mt-3">{asistencia?.totales?.ausente ?? 0}</p>
        </Card>
        <Card className="rounded-2xl p-5 border-border shadow-sm">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t("portal.reposiciones")}</p>
          <p className="font-display text-3xl font-extrabold mt-3">{asistencia?.totales?.reposicion ?? 0}</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,340px)_1fr] gap-6 mb-10">
        <BienestarEmocional bienestar={asistencia?.bienestar} />
        <div>
          <h3 className="font-display text-xl font-bold mb-2">{t("portal.aRecuperar")}</h3>
      <p className="text-sm text-muted-foreground mb-5 flex items-center gap-2">
        <Info className="h-4 w-4" />
        {asistencia?.con_derecho_reposicion
          ? "Elige día y hora con al menos 24 h de antelación, en una clase de la misma disciplina."
          : "Las recuperaciones están disponibles con el seguro de recuperación de clases."}
      </p>

      <div className="space-y-4 mb-10">
        {reposiciones.map((r) => (
          <Card key={r.id} className="rounded-2xl p-5 border-border shadow-sm" data-testid={`portal-reposicion-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="font-display font-bold">{r.clase || r.disciplina}</p>
                <p className="text-xs text-muted-foreground mt-1">Falta del {r.fecha_origen}</p>
              </div>
              <Badge variant={r.estado === "agendada" ? "default" : "secondary"} data-testid={`portal-repo-estado-${r.id}`}>
                {r.estado === "agendada" ? "Reservada" : r.estado === "pendiente" ? "Por recuperar" : r.estado}
              </Badge>
            </div>

            {r.estado === "agendada" && r.destino && (
              <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm flex items-center gap-2">
                  <CalendarClock className="h-4 w-4 text-primary" />
                  {r.destino.fecha} · {r.destino.hora_inicio}–{r.destino.hora_fin}
                </p>
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => cancelar(r.id)}
                  data-testid={`portal-cancelar-repo-${r.id}`}>
                  <X className="h-3.5 w-3.5 mr-1.5" /> Anular
                </Button>
              </div>
            )}

            {r.estado === "pendiente" && (
              <div className="mt-4">
                {!opciones[r.id] ? (
                  <Button variant="outline" size="sm" className="rounded-full" disabled={cargando === r.id}
                    onClick={() => cargarOpciones(r.id)} data-testid={`portal-ver-huecos-${r.id}`}>
                    {cargando === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CalendarClock className="h-3.5 w-3.5 mr-1.5" /> Elegir día y hora</>}
                  </Button>
                ) : opciones[r.id].opciones.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid={`portal-sin-huecos-${r.id}`}>
                    No hay huecos disponibles con 24 h de antelación. Contacta con tu sede.
                  </p>
                ) : (
                  <Select onValueChange={(v) => agendar(r.id, v)}>
                    <SelectTrigger className="rounded-lg" data-testid={`portal-select-hueco-${r.id}`}>
                      <SelectValue placeholder="Selecciona día y hora" />
                    </SelectTrigger>
                    <SelectContent>
                      {opciones[r.id].opciones.map((o) => (
                        <SelectItem key={o.sesion_id} value={o.sesion_id}>
                          {o.fecha} · {o.hora_inicio}–{o.hora_fin} · {o.clase} ({o.plazas_libres} plazas)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </Card>
        ))}
        {reposiciones.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="portal-sin-reposiciones">
            {asistencia?.con_derecho_reposicion
              ? 'No tienes clases pendientes de recuperar. Si te falta alguna, pulsa "Recuperar" en el historial de abajo.'
              : "No tienes clases pendientes de recuperar."}
          </p>
        )}
        </div>
      </div>
      </div>

      <h3 className="font-display text-xl font-bold mb-5">Historial de asistencia</h3>
      <div className="space-y-2">
        {(asistencia?.detalle || []).map((d, i) => {
          const e = ESTADO_LABEL[d.estado] || ESTADO_LABEL.presente;
          return (
            <Card key={i} className="rounded-xl p-4 border-border shadow-sm flex items-center justify-between gap-3"
              data-testid={`portal-asistencia-fila-${i}`}>
              <div>
                <p className="text-sm font-semibold">{d.clase || "Clase"}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{d.fecha}{d.nota ? ` · ${d.nota}` : ""}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${e.cls}`}>
                  {d.estado === "presente" && <Check className="h-3 w-3" />}
                  {e.label}
                </span>
                {d.recuperable && (
                  <Button size="sm" variant="outline" className="rounded-full font-semibold"
                    disabled={cargando === d.sesion_id}
                    onClick={() => pedirRecuperacion(d.sesion_id)}
                    data-testid={`portal-recuperar-${d.sesion_id}`}>
                    {cargando === d.sesion_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <><CalendarClock className="h-3.5 w-3.5 mr-1.5" /> Recuperar</>}
                  </Button>
                )}
                {d.repo_id && d.repo_estado === "pendiente" && (
                  <Badge variant="secondary" data-testid={`portal-repo-pendiente-${d.sesion_id}`}>
                    Por recuperar
                  </Badge>
                )}
              </div>
            </Card>
          );
        })}
        {(asistencia?.detalle || []).length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="portal-sin-asistencia">
            Aún no hay registros de asistencia.
          </p>
        )}
      </div>
    </div>
  );
};
